import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Users, Code, Bot, Terminal, MessageSquare, DollarSign, Database, TrendingUp, Zap } from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

const tools = [
  {
    name: 'GitHub Copilot',
    company: 'Microsoft',
    country: '🇺🇸',
    category: 'IDE Extension',
    description: 'Real-time AI code suggestions directly in your editor',
    strengths: ['Real-time suggestions', 'Multi-IDE support', 'GitHub integration'],
    keyStrength: 'IDE integration',
    useCase: 'Inline code completion',
    icon: Code,
    color: 'from-gray-600 to-gray-800',
    pricing: '$10-19',
    pricingPeriod: '/mo',
    pricingDetail: 'Free for students & OSS',
    context: '8K',
    contextLabel: 'tokens',
    users: '20M+',
    marketShare: '42%',
  },
  {
    name: 'ChatGPT',
    company: 'OpenAI',
    country: '🇺🇸',
    category: 'Chat Interface',
    description: 'Conversational AI with broad coding knowledge',
    strengths: ['Wide knowledge', 'Code explanation', 'Multi-language'],
    keyStrength: 'Broad knowledge',
    useCase: 'Learning & debugging',
    icon: MessageSquare,
    color: 'from-green-500 to-green-700',
    pricing: '$20',
    pricingPeriod: '/mo',
    pricingDetail: 'Plus plan with GPT-4o',
    context: '128K',
    contextLabel: 'tokens',
    users: '200M+',
    marketShare: 'Leader',
  },
  {
    name: 'Claude',
    company: 'Anthropic',
    country: '🇺🇸',
    category: 'Chat + API',
    description: 'Deep reasoning for complex code problems',
    strengths: ['200K context', 'Complex reasoning', 'Artifacts & projects'],
    keyStrength: 'Deep reasoning',
    useCase: 'Architecture & review',
    icon: Bot,
    color: 'from-orange-500 to-orange-700',
    pricing: '$20',
    pricingPeriod: '/mo',
    pricingDetail: 'Pro plan with full access',
    context: '200K',
    contextLabel: 'tokens',
    users: '10M+',
    marketShare: '53%*',
  },
  {
    name: 'Cursor',
    company: 'Anysphere',
    country: '🇺🇸',
    category: 'AI-First IDE',
    description: 'Full IDE with AI built into every feature',
    strengths: ['Codebase indexing', 'Chat + Composer', 'Multi-model support'],
    keyStrength: 'Full codebase',
    useCase: 'Full project development',
    icon: Terminal,
    color: 'from-electric-cyan to-blue-600',
    pricing: '$20',
    pricingPeriod: '/mo',
    pricingDetail: 'Pro plan unlimited',
    context: 'Full',
    contextLabel: 'codebase',
    users: '2M+',
    marketShare: '18%',
  },
]

export default function ToolsLandscapeSlide({ isActive: _isActive }: SlideProps) {
  const [selectedTool, setSelectedTool] = useState<number | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'compare'>('grid')

  return (
    <Slide>
      <StaggerContainer className="h-full flex flex-col">
        {/* Header */}
        <StaggerItem>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-2">
                AI Tools <span className="text-gradient">Landscape</span>
              </h2>
              <p className="text-lg text-white/60">
                Compare the leading AI coding assistants
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'grid'
                    ? 'bg-electric-cyan/20 text-electric-cyan'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                Grid View
              </button>
              <button
                onClick={() => setViewMode('compare')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'compare'
                    ? 'bg-electric-cyan/20 text-electric-cyan'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                Compare
              </button>
            </div>
          </div>
        </StaggerItem>

        {/* Tools Grid */}
        <StaggerItem className="flex-1">
          <AnimatePresence mode="wait">
            {viewMode === 'grid' ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full"
              >
                {tools.map((tool, index) => {
                  const Icon = tool.icon
                  return (
                    <motion.div
                      key={tool.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() =>
                        setSelectedTool(selectedTool === index ? null : index)
                      }
                      className={`glass rounded-2xl p-6 cursor-pointer transition-all duration-300 h-full flex flex-col ${
                        selectedTool === index ? 'ring-2 ring-electric-cyan glow-cyan' : ''
                      }`}
                    >
                      <motion.div
                        className="flex-1 flex flex-col"
                        animate={{
                          justifyContent: selectedTool === index ? 'flex-start' : 'center'
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Header with icon and name */}
                        <div className="flex items-start gap-4 mb-4">
                          <motion.div
                            className={`rounded-2xl bg-gradient-to-br ${tool.color} flex items-center justify-center flex-shrink-0 shadow-lg`}
                            animate={{
                              width: selectedTool === index ? 56 : 72,
                              height: selectedTool === index ? 56 : 72,
                            }}
                            transition={{ duration: 0.3 }}
                          >
                            <Icon className={`text-white transition-all duration-300 ${selectedTool === index ? 'w-7 h-7' : 'w-9 h-9'}`} />
                          </motion.div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-2xl font-bold text-white mb-1">
                              {tool.name}
                            </h3>
                            <div className="flex items-center gap-2 text-sm text-white/50">
                              <span>{tool.company}</span>
                              <span>{tool.country}</span>
                            </div>
                          </div>
                        </div>

                        {/* Stats row */}
                        <div className="flex items-center gap-3 mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${tool.color} text-white`}>
                            {tool.category}
                          </span>
                          <div className="flex items-center gap-1 text-white/60">
                            <Database className="w-4 h-4" />
                            <span className="text-sm font-mono">{tool.context}</span>
                            <span className="text-xs text-white/40">{tool.contextLabel}</span>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-base text-white/70 mb-4 leading-relaxed">
                          {tool.description}
                        </p>

                        {/* Pricing highlight */}
                        <div className="flex items-baseline gap-1 mt-auto">
                          <span className="text-2xl font-bold text-electric-cyan">{tool.pricing}</span>
                          <span className="text-sm text-white/40">{tool.pricingPeriod}</span>
                        </div>

                        {/* Expanded content */}
                        <AnimatePresence>
                          {selectedTool === index && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: 'easeOut' }}
                              className="overflow-hidden"
                            >
                              <div className="pt-4 mt-2">
                                <h4 className="text-sm text-white/40 uppercase tracking-wider mb-3">
                                  Key Strengths
                                </h4>
                                <div className="flex flex-wrap gap-2 mb-4">
                                  {tool.strengths.map((strength, i) => (
                                    <motion.span
                                      key={strength}
                                      initial={{ opacity: 0, scale: 0.8 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      transition={{ delay: i * 0.1 }}
                                      className="text-sm px-3 py-1.5 rounded-full bg-electric-cyan/10 text-electric-cyan"
                                    >
                                      {strength}
                                    </motion.span>
                                  ))}
                                </div>
                                <div className="flex items-center justify-between text-sm pt-3 border-t border-white/10">
                                  <div className="flex items-center gap-2">
                                    <Users className="w-5 h-5 text-white/40" />
                                    <span className="text-white/60">
                                      Best for: {tool.useCase}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <DollarSign className="w-5 h-5 text-white/40" />
                                    <span className="text-white/60">
                                      {tool.pricingDetail}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    </motion.div>
                  )
                })}
              </motion.div>
            ) : (
              <motion.div
                key="compare"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="glass rounded-2xl overflow-hidden"
              >
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 text-white/60 font-medium text-base">
                        Tool
                      </th>
                      <th className="text-left p-4 text-white/60 font-medium text-base hidden lg:table-cell">
                        <div className="flex items-center gap-1">
                          <Zap className="w-4 h-4" />
                          Strength
                        </div>
                      </th>
                      <th className="text-center p-4 text-white/60 font-medium text-base hidden md:table-cell">
                        <div className="flex items-center justify-center gap-1">
                          <Users className="w-4 h-4" />
                          Users
                        </div>
                      </th>
                      <th className="text-center p-4 text-white/60 font-medium text-base hidden sm:table-cell">
                        <div className="flex items-center justify-center gap-1">
                          <TrendingUp className="w-4 h-4" />
                          Share
                        </div>
                      </th>
                      <th className="text-right p-4 text-white/60 font-medium text-base">
                        <div className="flex items-center justify-end gap-1">
                          <DollarSign className="w-4 h-4" />
                          Price
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tools.map((tool, index) => {
                      const Icon = tool.icon
                      return (
                        <motion.tr
                          key={tool.name}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="border-b border-white/5 hover:bg-white/5 transition-colors"
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center`}
                              >
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <div className="font-semibold text-white text-lg">
                                  {tool.name}
                                </div>
                                <div className="text-xs text-white/50">
                                  {tool.company} {tool.country}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 hidden lg:table-cell">
                            <span className="text-electric-cyan text-sm font-medium">
                              {tool.keyStrength}
                            </span>
                          </td>
                          <td className="p-4 text-center hidden md:table-cell">
                            <span className="text-white font-bold text-lg">{tool.users}</span>
                          </td>
                          <td className="p-4 text-center hidden sm:table-cell">
                            <span className="text-white/70 text-sm font-mono">{tool.marketShare}</span>
                          </td>
                          <td className="p-4 text-right">
                            <span className="text-electric-cyan font-bold text-lg">{tool.pricing}</span>
                            <span className="text-white/40 text-xs">{tool.pricingPeriod}</span>
                          </td>
                        </motion.tr>
                      )
                    })}
                  </tbody>
                </table>
              </motion.div>
            )}
          </AnimatePresence>
        </StaggerItem>

        {/* Tip */}
        <StaggerItem>
          <div className="mt-4 mb-[100px] text-center">
            <motion.div
              className="text-base text-white/40"
              animate={{ opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {viewMode === 'grid' ? 'Tap any card to explore details' : '*Claude adoption rate among AI tools'}
            </motion.div>
            {viewMode === 'compare' && (
              <p className="text-xs text-white/30 mt-2">
                Data: TechCrunch 2025, Stack Overflow Survey, GitHub Research
              </p>
            )}
          </div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
